import { Checkbox, Switch, Tooltip } from 'antd';
import { camelCase } from 'lodash';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { styleTooltip } from '@/components/app-tooltip/AppTooltip';

import {
  COPY_ACTION_CELL_WIDTH,
  QUESTION_ACTION_CELL_WIDTH,
  TRANSLATE_CELL_WIDTH,
  TYPE_SHARE_DATA_BETWEEN_LANGUAGES,
  additionalFieldPostal
} from '../constants';
import { mappingRenderFunction } from '../helpers';
import { CONTENT_TYPE, IQuestion, IQuestionOption } from '../modals';
import Table, { ITableItem } from './types/Table';
import Text from './types/Text';
import TranslateFlag from './types/TranslateFlg';

interface IQuestionContentProps {
  question: IQuestion;
  mode: 'view' | 'edit';
  name: string;
  showTranslationData?: boolean;
  showTranslateFlg?: boolean;
  showCopyFlg?: boolean;
  isAncestor?: boolean;
  cellWidth?: number;
  showButtonChangeVisibleExternal?: boolean;
  copyAnswerFromApplicant?: (checked: boolean, name: string[]) => void;
  isTranslateData?: boolean;
}

const QuestionContent: React.FC<IQuestionContentProps> = ({
  isAncestor,
  cellWidth,
  question,
  mode,
  name,
  showTranslationData,
  showTranslateFlg,
  showCopyFlg,
  isTranslateData,
  showButtonChangeVisibleExternal,
  copyAnswerFromApplicant
}) => {
  const isViewMode = mode === 'view';
  const { t } = useTranslation();
  const form = useFormContext();
  const getTextPair = (questions: IQuestion[]): { [key: string]: { left?: IQuestion; right?: IQuestion } } => {
    const listTextPair = questions
      .map((i, index) => ({ ...i, index }))
      .filter((i) => [CONTENT_TYPE.TEXTPAIR_LEFT, CONTENT_TYPE.TEXTPAIR_RIGHT].includes(i.type) && i.level !== 1);
    if (listTextPair.length) {
      const pairDict = listTextPair.reduce((dict: { [key: string]: { left?: IQuestion; right?: IQuestion } }, cur) => {
        if (cur.type === CONTENT_TYPE.TEXTPAIR_LEFT && cur.id) {
          dict[cur.id] = {
            ...dict[cur.id],
            left: { ...cur, no: cur.index }
          };
        }
        if (cur.type === CONTENT_TYPE.TEXTPAIR_RIGHT && cur.pairId) {
          dict[cur.pairId] = {
            ...dict[cur.pairId],
            right: { ...cur, no: cur.index }
          };
        }
        return dict;
      }, {});

      return pairDict;
    }
    return {};
  };

  const setExternalVisible = (fieldNames: string[], checked: boolean) => {
    fieldNames.forEach((fieldName) => {
      form.setValue(`${fieldName ?? ''}.visibleExternalUser`, checked, {
        shouldDirty: true,
        shouldTouch: true
      });
    });
  };

  const generateCellAdditions = (
    items: {
      key: 'copy' | 'translate' | 'externalVisible';
      fieldNames: string[];
      isEmptyField?: boolean;
    }[]
  ): ITableItem[] => {
    const additionalCells: ITableItem[] = [];

    items.forEach((item) => {
      if (item.key === 'copy') {
        additionalCells.push({
          content: item?.isEmptyField ? (
            <></>
          ) : (
            <Checkbox
              onChange={(e) => {
                if (isViewMode) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }

                copyAnswerFromApplicant?.(e.target.checked, item.fieldNames);
              }}
              checked={!!form.getValues(`${item.fieldNames[0] ?? ''}.answer.isCopyAnswer`)}
            >
              <Tooltip title={t('project:question_detail:copy_from_applicant_tooltip')} {...styleTooltip}>
                {t('project:question_detail:copy_from_applicant')}
              </Tooltip>
            </Checkbox>
          ),
          style: { width: `${COPY_ACTION_CELL_WIDTH}px` }
        });
      }

      if (item.key === 'translate') {
        additionalCells.push({
          content: item?.isEmptyField ? <></> : <TranslateFlag translateFlag={!!form.getValues(`${item.fieldNames[0] ?? ''}.translateFlag`)} />,
          style: { width: `${TRANSLATE_CELL_WIDTH}px` }
        });
      }

      if (item.key === 'externalVisible') {
        additionalCells.push({
          content: item?.isEmptyField ? (
            <></>
          ) : (
            <Switch
              onClick={(checked) => {
                setExternalVisible(item.fieldNames, checked);
              }}
              disabled={form.getValues(`${item.fieldNames[0] ?? ''}._disabled`)}
              checked={!!form.getValues(`${item.fieldNames[0] ?? ''}.visibleExternalUser`)}
            />
          ),
          style: {
            width: `${QUESTION_ACTION_CELL_WIDTH}px`
          },
          className: `max-w-[${QUESTION_ACTION_CELL_WIDTH}px] text-center`
        });
      }
    });

    return additionalCells.map((i) => ({
      ...i,
      className: 'additional-cell'
    }));
  };

  const safeParse = (value: any) => {
    try {
      if (!value) return {};

      if (typeof value === 'object') return value;

      return JSON.parse(value);
    } catch (e) {
      console.error('Parse error:', value, e);
      return {};
    }
  };

  useEffect(() => {
    if (question.type !== CONTENT_TYPE.POSTAL_CODE) return;

    const currentAnswered = form.getValues(`${name}.answer.answered`);

    const currentAnsweredEn = form.getValues(`${name}.answer.answeredEn`);

    if (!currentAnswered && question.answer?.answered) {
      form.setValue(`${name}.answer.answered`, safeParse(question.answer.answered), {
        shouldDirty: false,
        shouldTouch: false
      });
    }

    if (!currentAnsweredEn && question.answer?.answeredEn) {
      form.setValue(`${name}.answer.answeredEn`, safeParse(question.answer.answeredEn), {
        shouldDirty: false,
        shouldTouch: false
      });
    }
  }, []);

  const renderQuestionRow = ({
    questionRender,
    isTranslate,
    containCellTranslateStatus = false,
    containCellCopied = false,
    containCellVisible = false
  }: {
    questionRender: IQuestion;
    isTranslate: boolean;
    containCellTranslateStatus?: boolean;
    containCellCopied?: boolean;
    containCellVisible?: boolean;
  }) => {
    if (questionRender?.isEmptyField && isTranslate) return [];
    const headerFontClass = 'body-700 whitespace-pre-line';
    const headerClass = `!w-[150px]`;
    const row: ITableItem[] = [];
    const subTableContent: ITableItem[][] = [];
    const answerKey = isTranslate ? 'answeredEn' : 'answered';
    const shouldShowActionCells = showTranslateFlg || showCopyFlg || showButtonChangeVisibleExternal;

    const renderFunc = mappingRenderFunction[questionRender.type ?? ''];
    const question: IQuestion = {
      ...questionRender,
      labelInput:
        questionRender.type === CONTENT_TYPE.DATETIMEFROM_TO
          ? [t('project:question_detail:type:date_time_from'), t('project:question_detail:type:date_time_to')]
          : questionRender.labelInput
    };
    // Flag to check if should add translate flag cell on the end of question content
    let shouldAddAdditionalCell = showTranslateFlg || showCopyFlg || showButtonChangeVisibleExternal;

    // If question is not level 1, create a title cell
    if (question.level !== 1 && question.type !== CONTENT_TYPE.NONE) {
      row.push({
        isTitle: true,
        className: headerClass,
        content: <p className={headerFontClass}>{question.title}</p>
      });
    }

    // Get selected item if question is radio
    const { selectedItem, selectedIndex } =
      question?.options?.reduce(
        (
          values: {
            selectedItem: IQuestionOption | undefined;
            selectedIndex: number | undefined;
          },
          item,
          index
        ) => {
          if (item.id === question.answer?.answered?.id) {
            return { selectedItem: item, selectedIndex: index };
          }
          return values;
        },
        {
          selectedItem: undefined,
          selectedIndex: undefined
        }
      ) ?? {};

    const additionalItems: ('copy' | 'translate' | 'externalVisible')[] = [];

    if (shouldShowActionCells) {
      if (showCopyFlg) additionalItems.push('copy');
      if (showTranslateFlg) additionalItems.push('translate');
      if (showButtonChangeVisibleExternal) additionalItems.push('externalVisible');
    }

    if (question.remark && (!isTranslate || question.remarkEn)) {
      subTableContent.push([
        {
          content: (
            <Table
              items={[
                [
                  {
                    content: <p className='font-bold'>{isTranslate ? question.remarkEn || question.remark : question.remark}</p>
                  }
                ]
              ]}
              className='sub-table'
            />
          )
        },
        ...generateCellAdditions(additionalItems.map((key) => ({ key, fieldNames: [], isEmptyField: true })))
      ]);
      shouldAddAdditionalCell = false;
    }
    if (renderFunc) {
      const shouldShareData = TYPE_SHARE_DATA_BETWEEN_LANGUAGES.includes(question.type) && question.type !== CONTENT_TYPE.POSTAL_CODE;

      const key = `${name}.answer.${shouldShareData ? 'answered' : answerKey}`;
      const itemsRender: ITableItem[] = renderFunc({
        question,
        readOnly: isViewMode,
        name: key,
        isTranslateData: isTranslate,
        disabledDateFrom:
          question.type !== CONTENT_TYPE.DATETIMEFROM_TO
            ? undefined
            : (d) => {
                return form.getValues(`${key}.to`) && d.startOf('day').isAfter(form.getValues(`${key}.to`));
              },
        disabledDateTo:
          question.type !== CONTENT_TYPE.DATETIMEFROM_TO
            ? undefined
            : (d) => {
                return form.getValues(`${key}.from`) && d.startOf('day').isBefore(form.getValues(`${key}.from`));
              },
        showTranslateData: showTranslationData,
        onChangeUnit(_value, label) {
          form.setValue(`${name}.answer.answered.unit.title`, label, { shouldDirty: true });
        }
      }).map((i) => ({ content: i }));
      const postalItems: any[] = [];
      const isPostal = question.type === CONTENT_TYPE.POSTAL_CODE;

      if (isPostal) {
        itemsRender.unshift({
          isTitle: true,
          className: headerClass,
          content: <p className={headerFontClass}>{t('postal_code:title')}</p>
        });

        additionalFieldPostal.forEach(({ key, type: fieldType }) => {
          const fieldKey = camelCase(key);

          const postalName = isTranslate ? `${name}.answer.answeredEn.${fieldKey}` : `${name}.answer.answered.${fieldKey}`;

          postalItems.push([
            {
              content: (
                <Table
                  items={[
                    [
                      {
                        isTitle: true,
                        className: headerClass,
                        content: <p className={headerFontClass}>{t(`postal_code:${key}`)}</p>
                      },
                      {
                        content: (
                          <Text type={fieldType} placeholder={t(`postal_code:${key}_placeholder`) ?? ''} name={postalName} readOnly={isViewMode} />
                        )
                      }
                    ]
                  ]}
                  className='sub-table'
                />
              )
            }
          ]);
        });
      }

      const shouldRenderActionCells = showTranslateFlg || showCopyFlg || showButtonChangeVisibleExternal;

      if (shouldRenderActionCells) {
        shouldAddAdditionalCell = false;

        subTableContent.push([
          {
            content: <Table items={[[...itemsRender], ...postalItems]} className='sub-table' />
          },
          ...generateCellAdditions(
            additionalItems.map((key) => ({
              key,
              fieldNames: [name]
            }))
          )
        ]);
      } else {
        subTableContent.push([
          {
            content: <Table items={[[...itemsRender], ...postalItems]} className='sub-table' />
          },
          ...(shouldRenderActionCells
            ? generateCellAdditions(
                additionalItems.map((key) => ({
                  key,
                  fieldNames: [name]
                }))
              )
            : [])
        ]);
      }
    }

    if (question.type === CONTENT_TYPE.RADIO) {
      if (selectedItem?.questions?.length) {
        const fieldName = `${name}.options.${selectedIndex}`;
        const textPair = getTextPair(selectedItem.questions);

        selectedItem.questions.forEach((q, qIndex) => {
          if (q.type === CONTENT_TYPE.TEXTPAIR_LEFT) {
            const leftItem = textPair[q?.id ?? '']?.left;
            const rightItem = textPair[q?.id ?? '']?.right;

            const leftName = `${fieldName}.questions.${qIndex}`;
            const rightName = rightItem ? `${fieldName}.questions.${rightItem.no}` : '';

            // original row
            subTableContent.push(
              renderTextPairRow({
                leftItem,
                rightItem,
                leftName,
                rightName,
                isTranslate: false,
                additionalItems
              })
            );

            // translate row
            const shouldRenderTranslate = showTranslationData && (leftItem?.translateFlag || rightItem?.translateFlag);

            if (shouldRenderTranslate) {
              subTableContent.push(
                renderTextPairRow({
                  leftItem,
                  rightItem,
                  leftName,
                  rightName,
                  isTranslate: true,
                  additionalItems
                })
              );
            }

            return;
          }

          if (q.type === CONTENT_TYPE.TEXTPAIR_RIGHT) {
            return;
          }
          subTableContent.push([
            {
              className: 'w-full child-table',
              content: (
                <QuestionContent
                  showTranslateFlg={containCellTranslateStatus}
                  showCopyFlg={containCellCopied}
                  key={q.id}
                  question={q}
                  mode={mode}
                  name={`${fieldName}.questions.${qIndex}`}
                  copyAnswerFromApplicant={copyAnswerFromApplicant}
                  isTranslateData={isTranslate}
                  showTranslationData={showTranslationData && !question.translateFlag}
                  showButtonChangeVisibleExternal={containCellVisible}
                />
              )
            }
          ]);
        });
      }
    }

    if (question.questions?.length) {
      const textPair = getTextPair(question.questions);
      question.questions.forEach((q, qIndex) => {
        if (q.type === CONTENT_TYPE.TEXTPAIR_LEFT) {
          const leftItem = textPair[q?.id ?? '']?.left;
          const rightItem = textPair[q?.id ?? '']?.right;

          const leftName = `${name}.questions.${qIndex}`;
          const rightName = rightItem ? `${name}.questions.${rightItem.no}` : '';

          // original row
          subTableContent.push(
            renderTextPairRow({
              leftItem,
              rightItem,
              leftName,
              rightName,
              isTranslate: false,
              additionalItems
            })
          );

          // translate row
          const shouldRenderTranslate = showTranslationData && (leftItem?.translateFlag || rightItem?.translateFlag);

          if (shouldRenderTranslate) {
            subTableContent.push(
              renderTextPairRow({
                leftItem,
                rightItem,
                leftName,
                rightName,
                isTranslate: true,
                additionalItems
              })
            );
          }

          shouldAddAdditionalCell = false;
          return;
        }

        if (q.type === CONTENT_TYPE.TEXTPAIR_RIGHT) {
          return;
        }
        subTableContent.push([
          {
            className: 'child-table',
            content: (
              <QuestionContent
                showTranslateFlg={containCellTranslateStatus}
                showCopyFlg={containCellCopied}
                question={q}
                mode={mode}
                name={`${name}.questions.${qIndex}`}
                copyAnswerFromApplicant={copyAnswerFromApplicant}
                isTranslateData={isTranslate}
                showTranslationData={showTranslationData && !question.translateFlag}
                showButtonChangeVisibleExternal={containCellVisible}
              />
            )
          }
        ]);
      });
    }

    const mainTable = <Table className='sub-table' items={subTableContent} />;

    row.push({
      content: mainTable
    });

    const shouldHideOuterActionCells = question.type === CONTENT_TYPE.RADIO;

    if (shouldAddAdditionalCell && shouldHideOuterActionCells) {
      row.push(
        ...generateCellAdditions(
          additionalItems.map((key) => ({
            key,
            fieldNames: [name]
          }))
        )
      );
    }
    return row;
  };

  const renderTextPairRow = ({
    leftItem,
    rightItem,
    leftName,
    rightName,
    isTranslate,
    additionalItems
  }: {
    leftItem?: IQuestion;
    rightItem?: IQuestion;
    leftName: string;
    rightName?: string;
    isTranslate: boolean;
    additionalItems: ('copy' | 'translate' | 'externalVisible')[];
  }): ITableItem[] => {
    const answerKey = isTranslate ? 'answeredEn' : 'answered';

    return [
      {
        content: (
          <Table
            className='sub-table'
            items={[
              [
                {
                  content: leftItem ? (
                    <Text
                      name={`${leftName}.answer.${answerKey}`}
                      labelInput={leftItem.title}
                      labelClassName='body-700 text-nowrap'
                      readOnly={isViewMode}
                      type={isTranslate ? undefined : leftItem.answerLanguage}
                      placeholder={leftItem.placeHolder}
                    />
                  ) : (
                    <></>
                  )
                },
                {
                  content:
                    rightItem && rightName ? (
                      <Text
                        name={`${rightName}.answer.${answerKey}`}
                        labelInput={rightItem.title}
                        labelClassName='body-700 text-nowrap'
                        readOnly={isViewMode}
                        type={isTranslate ? undefined : rightItem.answerLanguage}
                        placeholder={rightItem.placeHolder}
                      />
                    ) : (
                      <></>
                    )
                },
                ...generateCellAdditions(
                  additionalItems.map((key) => ({
                    key,
                    fieldNames: [leftName, rightName ?? '']
                  }))
                )
              ]
            ]}
          />
        )
      }
    ];
  };

  const renderChildren = (questionRender: IQuestion) => {
    const needRenderTranslate = showTranslationData;

    const currentIsTranslate = isTranslateData === undefined && isAncestor ? false : !!isTranslateData;

    const rows: ITableItem[][] = [];

    // original row
    rows.push(
      renderQuestionRow({
        questionRender,
        containCellTranslateStatus: showTranslateFlg,
        containCellCopied: showCopyFlg,
        isTranslate: currentIsTranslate,
        containCellVisible: showButtonChangeVisibleExternal
      })
    );

    // translate row
    if (needRenderTranslate && questionRender.translateFlag && !currentIsTranslate) {
      rows.push(
        renderQuestionRow({
          questionRender,
          containCellTranslateStatus: showTranslateFlg,
          containCellCopied: showCopyFlg,
          isTranslate: true,
          containCellVisible: showButtonChangeVisibleExternal
        })
      );
    }

    if (rows.length === 2 && isAncestor) {
      rows[0] = rows[0].map((item) => ({
        ...item,
        style: { ...item.style, width: `${cellWidth || 0}px` }
      }));
    }

    return <Table className='sub-table' items={rows} />;
  };

  return (
    <>
      {/* Render other components based on type */}
      {renderChildren(question)}
    </>
  );
};

export default QuestionContent;
